// frontend/src/hooks/useAuthRedirectHandler.ts
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingAction, clearPendingAction, getReturnUrl } from '../utils/authRedirectState';
import { logger } from '../services/logger/BrowserLogger';

/**
 * Hook to handle navigation and pending actions after OAuth redirect.
 * Must be used at the top level of the app (in App.tsx).
 *
 * This hook:
 * 1. Listens for auth-redirect-complete event from FirebaseAuthContext
 * 2. Navigates back to the original page
 * 3. Re-dispatches event with pending action for components to handle
 */
export function useAuthRedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthRedirect = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { user, pendingAction: eventPendingAction } = customEvent.detail;

      // Guard: Don't process re-dispatched events (events that already have pendingAction in detail)
      // Those are meant for component handlers, not this navigation handler
      if (eventPendingAction) {
        return;
      }

      // Get the return URL
      const returnUrl = getReturnUrl();

      // Get pending action if any
      const pendingAction = getPendingAction();

      logger.info('Handling auth redirect', {
        userEmail: user?.email,
        returnUrl,
        hasPendingAction: !!pendingAction,
        actionType: pendingAction?.type
      });

      // Navigate back to the original page
      if (returnUrl && returnUrl !== window.location.pathname) {
        navigate(returnUrl, { replace: true });
      }

      // Re-dispatch event with pending action for components to handle
      // Use setTimeout to ensure the navigated page has mounted
      if (pendingAction) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('auth-redirect-complete', {
            detail: { user, pendingAction }
          }));

          logger.info('Dispatched pending action event', {
            actionType: pendingAction.type,
            returnUrl
          });

          // Clear the pending action after dispatching
          setTimeout(() => {
            clearPendingAction();
          }, 1000);
        }, 150); // Delay to allow component mounting after navigation
      } else {
        clearPendingAction();
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => {
      window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
    };
  }, [navigate]);
}
